/**
 * Tax purposes ref management (Підпорція 3.1a, 2026-05-24).
 *
 * Global довідник з 5 seed-записами (TAXABLE/EXEMPT/NOT_OBJECT/EXPORT/MIXED).
 * Користувач може додавати власні значення; seed-записи захищені від
 * delete на backend (handler 409).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taxPurposes, type TaxPurpose } from '@/api/taxation';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function TaxPurposesPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const list = useQuery({
    queryKey: ['tax-purposes'],
    queryFn: () => taxPurposes.list(),
  });

  const [editing, setEditing] = useState<TaxPurpose | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name_uk: '', description: '' });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      taxPurposes.create({
        code: form.code,
        name_uk: form.name_uk,
        description: form.description || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-purposes'] });
      setCreating(false);
      setForm({ code: '', name_uk: '', description: '' });
    },
    onError: (e) => setError(extractError(e)),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      taxPurposes.update(editing!.id, {
        name_uk: form.name_uk,
        description: form.description || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-purposes'] });
      setEditing(null);
    },
    onError: (e) => setError(extractError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => taxPurposes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tax-purposes'] }),
    onError: (e) => setError(extractError(e)),
  });

  function startEdit(p: TaxPurpose) {
    setEditing(p);
    setCreating(false);
    setForm({ code: p.code, name_uk: p.name_uk, description: p.description ?? '' });
    setError(null);
  }

  const cols = useMemo<ColumnDef<TaxPurpose>[]>(() => [
    {
      key: 'code', header: 'Код', width: 130,
      sortValue: (p) => p.code ?? '',
      cell: (p) => <code>{p.code}</code>,
    },
    {
      key: 'name_uk', header: 'Назва', width: 250,
      sortValue: (p) => (p.name_uk ?? '').toLowerCase(),
      cell: (p) => p.name_uk,
    },
    {
      key: 'description', header: 'Опис',
      sortValue: (p) => (p.description ?? '').toLowerCase(),
      cell: (p) => p.description,
    },
    {
      key: 'seed_source', header: 'Джерело', width: 160,
      sortValue: (p) => p.seed_source ?? '',
      cell: (p) => <small>{p.seed_source}</small>,
    },
    {
      key: 'is_active', header: 'Активний', width: 100, align: 'center',
      sortValue: (p) => (p.is_active ? 1 : 0),
      cell: (p) => (p.is_active ? '✓' : '—'),
    },
    {
      key: 'actions', header: 'Дії', width: 90, align: 'right',
      cell: (p) => (
        <>
          <button onClick={() => startEdit(p)}>✎</button>
          <button
            className="danger"
            style={{ marginLeft: '0.3rem' }}
            disabled={p.seed_source?.startsWith('Internal')}
            onClick={() =>
              confirm('Видалити запис?').then((ok) => ok && deleteMut.mutate(p.id))
            }
          >
            ×
          </button>
        </>
      ),
    },
  ], [confirm, deleteMut]);

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Податкові призначення</h2>
        <button
          className="primary"
          onClick={() => {
            setCreating(true);
            setEditing(null);
            setForm({ code: '', name_uk: '', description: '' });
            setError(null);
          }}
        >
          + Додати
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', padding: '0.5rem' }}>{error}</div>}

      {(creating || editing) && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h3>{editing ? 'Редагування' : 'Нове податкове призначення'}</h3>
          <div className="field">
            <label>Код</label>
            <input
              value={form.code}
              disabled={!!editing}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              data-testid="tp-code"
            />
          </div>
          <div className="field">
            <label>Назва</label>
            <input
              value={form.name_uk}
              onChange={(e) => setForm({ ...form, name_uk: e.target.value })}
              data-testid="tp-name"
            />
          </div>
          <div className="field">
            <label>Опис</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <button onClick={() => { setCreating(false); setEditing(null); }}>Скасувати</button>
            <button
              className="primary"
              onClick={() => editing ? updateMut.mutate() : createMut.mutate()}
              disabled={!form.code || !form.name_uk}
            >
              Зберегти
            </button>
          </div>
        </div>
      )}

      <div data-testid="tp-table">
        <DataTable<TaxPurpose>
          rows={list.data ?? []}
          columns={cols}
          rowKey={(p) => p.id}
          loading={list.isLoading}
          emptyText="Немає податкових призначень."
          initialSortKey="code"
        />
      </div>
    </div>
  );
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return 'Помилка';
}
